<template>
  <div class="pwa-root">
    <!-- Header -->
    <div class="pwa-header">
      <div class="pwa-header__left">
        <button v-if="showBack" class="pwa-back-btn" @click="$router.back()">
          <span class="mdi mdi-arrow-left"></span>
        </button>
        <template v-else>
          <div class="pwa-logo">
            <img :src="logoWhite" class="pwa-logo-img" alt="Gwassou" />
          </div>
          <div class="pwa-brand">
            <span class="pwa-brand__name">Gwassou</span>
            <span class="pwa-brand__sub">{{ subtitle }}</span>
          </div>
        </template>
        <div v-if="showBack" class="pwa-page-title">
          <span class="pwa-page-title__main">{{ title }}</span>
          <span class="pwa-page-title__sub">{{ subtitle }}</span>
        </div>
      </div>
      <div class="pwa-header__right">
        <slot name="header-action">
          <button class="pwa-icon-btn" @click="$emit('bell-click')">
            <span class="mdi mdi-bell-outline"></span>
            <span v-if="hasNotif" class="pwa-notif-dot"></span>
          </button>
        </slot>
      </div>
    </div>

    <!-- Content -->
    <div class="pwa-content">
      <slot />
    </div>

    <!-- FAB & overlays (rendus hors de .pwa-content pour éviter les conflits de scroll) -->
    <slot name="fab" />

    <!-- Install Banner -->
    <pwa-install-banner />

    <!-- Bottom Navigation (filtree selon les capacites) -->
    <nav class="pwa-bottom-nav">
      <router-link
        v-if="caps.canSeeHome"
        to="/pwa/home"
        class="pwa-nav-item"
        active-class="pwa-nav-item--active"
        exact
      >
        <span class="mdi mdi-home-variant-outline pwa-nav-icon"></span>
        <span class="pwa-nav-label">Accueil</span>
      </router-link>
      <router-link
        v-if="caps.canSeeVehicles"
        to="/pwa/vehicles"
        class="pwa-nav-item"
        active-class="pwa-nav-item--active"
      >
        <span class="mdi mdi-car-outline pwa-nav-icon"></span>
        <span class="pwa-nav-label">Véhicules</span>
      </router-link>
      <router-link
        v-if="caps.canSeeVouchers"
        to="/pwa/vouchers"
        class="pwa-nav-item"
        active-class="pwa-nav-item--active"
      >
        <span class="mdi mdi-gas-station pwa-nav-icon"></span>
        <span class="pwa-nav-label">Carburant</span>
      </router-link>
      <router-link
        v-if="caps.canSeeTransactions"
        to="/pwa/history"
        class="pwa-nav-item"
        active-class="pwa-nav-item--active"
      >
        <span class="mdi mdi-history pwa-nav-icon"></span>
        <span class="pwa-nav-label">Historique</span>
      </router-link>
      <router-link to="/pwa/profile" class="pwa-nav-item" active-class="pwa-nav-item--active">
        <span class="mdi mdi-account-outline pwa-nav-icon"></span>
        <span class="pwa-nav-label">Profil</span>
      </router-link>
    </nav>
  </div>
</template>

<script>
import { icon_logo_white } from '@/assets/img/custom'
import PwaInstallBanner from './PwaInstallBanner.vue'

export default {
  name: 'PwaLayout',
  components: { PwaInstallBanner },
  props: {
    title: { type: String, default: '' },
    subtitle: { type: String, default: 'ESPACE PRO' },
    showBack: { type: Boolean, default: false },
    hasNotif: { type: Boolean, default: false },
  },
  data() {
    return {
      logoWhite: icon_logo_white,
    }
  },
  computed: {
    caps() {
      return this.$store.getters['app/pwaCapabilities']
    },
  },
  created() {
    // Charger les privileges utilisateur si pas deja en cache
    // (necessaire pour que pwaCapabilities soit complet pour les operateurs)
    if (this.$store.state.app.userPrivileges === null) {
      this.$store.dispatch('app/loadUserPrivileges')
    }
  },
}
</script>

<style>
/* ── Variables ── */
:root {
  --pwa-bg: #F0F4F8;
  --pwa-white: #FFFFFF;
  --pwa-dark: #1C2437;
  --pwa-accent: #00B4D8;
  --pwa-text: #1A1A2E;
  --pwa-muted: #8892A4;
  --pwa-success: #4CAF50;
  --pwa-warning: #FF8C00;
  --pwa-error: #F44336;
  --pwa-card-radius: 16px;
  --pwa-header-h: 64px;
  --pwa-nav-h: 64px;
}
</style>

<style scoped>
.pwa-root {
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  background: var(--pwa-bg);
  font-family: 'Segoe UI', system-ui, sans-serif;
  max-width: 480px;
  margin: 0 auto;
  position: relative;
}

/* ── Header ── */
.pwa-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--pwa-white);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  height: var(--pwa-header-h);
  box-shadow: 0 1px 0 rgba(0,0,0,0.06);
}
.pwa-header__left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.pwa-logo {
  width: 40px;
  height: 40px;
  background: var(--pwa-dark);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pwa-logo-img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}
.pwa-brand__name {
  display: block;
  font-weight: 700;
  font-size: 17px;
  color: var(--pwa-text);
  line-height: 1.2;
}
.pwa-brand__sub {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: var(--pwa-accent);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.pwa-back-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1.5px solid #E0E6ED;
  background: var(--pwa-white);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: var(--pwa-text);
  cursor: pointer;
  flex-shrink: 0;
}
.pwa-page-title__main {
  display: block;
  font-weight: 700;
  font-size: 17px;
  color: var(--pwa-text);
  line-height: 1.2;
}
.pwa-page-title__sub {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: var(--pwa-accent);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.pwa-icon-btn {
  position: relative;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  font-size: 22px;
  color: var(--pwa-text);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pwa-notif-dot {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 8px;
  height: 8px;
  background: var(--pwa-error);
  border-radius: 50%;
  border: 1.5px solid white;
}

/* ── Content ── */
.pwa-content {
  flex: 1;
  overflow-y: auto;
  padding-bottom: calc(var(--pwa-nav-h) + 8px);
}

/* ── Bottom Nav ── */
.pwa-bottom-nav {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  height: var(--pwa-nav-h);
  background: var(--pwa-white);
  display: flex;
  align-items: center;
  justify-content: space-around;
  box-shadow: 0 -1px 0 rgba(0,0,0,0.07), 0 -4px 16px rgba(0,0,0,0.06);
  z-index: 100;
}
.pwa-nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  flex: 1;
  text-decoration: none;
  color: var(--pwa-muted);
  padding: 8px 0;
  transition: color 0.2s;
}
.pwa-nav-item--active {
  color: var(--pwa-accent);
}
.pwa-nav-icon {
  font-size: 22px;
}
.pwa-nav-label {
  font-size: 11px;
  font-weight: 500;
}
</style>
